import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import './Projects.css';

const Projects = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const autoplayRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const videoRef = useRef(null);

  // Gestión Digital carousel state
  const [activeGestionIndex, setActiveGestionIndex] = useState(0);
  const [isGestionPaused, setIsGestionPaused] = useState(false);
  const [gestionTouchStart, setGestionTouchStart] = useState(0);
  const gestionAutoplayRef = useRef(null);
  const gestionPauseTimeoutRef = useRef(null);

  const implementationAssets = [
    { 
      url: '/flujo-assets/website.png', 
      label: 'Sitio web',
      type: 'image',
      category: 'SITIO WEB',
      title: 'Sitio web',
      description: 'Vitrina digital que conecta servicios, proyectos y canales de contacto de Flujo SpA.'
    },
    { 
      url: '/flujo-assets/insta-1.jpg', 
      label: 'Instagram',
      type: 'image',
      category: 'PRESENCIA DIGITAL',
      title: 'Instagram',
      description: 'Contenido y presencia activa para mostrar proyectos y generar nuevos puntos de contacto.'
    },
    { 
      url: '/flujo-assets/insta-2.jpg', 
      label: 'Instagram',
      type: 'image',
      category: 'PRESENCIA DIGITAL',
      title: 'Instagram',
      description: 'Contenido y presencia activa para mostrar proyectos y generar nuevos puntos de contacto.'
    },
    { 
      url: '/flujo-assets/facebook-1.jpg', 
      label: 'Facebook',
      type: 'image',
      category: 'PRESENCIA DIGITAL',
      title: 'Facebook',
      description: 'Canal conectado para mantener contenido, información comercial y contacto alineados.'
    },
    { 
      url: '/flujo-assets/facebook-2.jpg', 
      label: 'Facebook',
      type: 'image',
      category: 'PRESENCIA DIGITAL',
      title: 'Facebook',
      description: 'Canal conectado para mantener contenido, información comercial y contacto alineados.'
    },
    { 
      url: '/flujo-assets/meta-business-1.png', 
      label: 'Meta Business Suite',
      type: 'image',
      category: 'GESTIÓN DE CANALES',
      title: 'Meta Business Suite',
      description: 'Instagram y Facebook centralizados para gestionar contenido, publicaciones y mensajes.'
    },
    { 
      url: '/flujo-assets/meta-business-2.png', 
      label: 'Meta Business Suite',
      type: 'image',
      category: 'GESTIÓN DE CANALES',
      title: 'Meta Business Suite',
      description: 'Instagram y Facebook centralizados para gestionar contenido, publicaciones y mensajes.'
    },
    { 
      url: '/flujo-assets/google-business-1.png', 
      label: 'Google Business',
      type: 'image',
      category: 'PRESENCIA DIGITAL',
      title: 'Google Business',
      description: 'Presencia optimizada para que Flujo SpA pueda ser encontrado y contactado desde Google.'
    },
    { 
      url: '/flujo-assets/google-business-2.png', 
      label: 'Google Business',
      type: 'image',
      category: 'PRESENCIA DIGITAL',
      title: 'Google Business',
      description: 'Presencia optimizada para que Flujo SpA pueda ser encontrado y contactado desde Google.'
    }
  ];

  const gestionDigitalAssets = [
    { 
      url: '/flujo-gestion/paso1-forms.png', 
      label: 'Microsoft Forms',
      type: 'image',
      step: 1,
      stepLabel: 'Formulario',
      title: 'Microsoft Forms',
      description: 'Formulario digital para capturar solicitudes de clientes con datos estructurados y archivos adjuntos.'
    },
    { 
      url: '/flujo-gestion/paso2-lists.png', 
      label: 'SharePoint Lists',
      type: 'image',
      step: 2,
      stepLabel: 'Registro',
      title: 'SharePoint Lists',
      description: 'Base de datos central donde se registran automáticamente todas las solicitudes recibidas.'
    },
    { 
      url: '/flujo-gestion/paso3-onedrive.png', 
      label: 'OneDrive',
      type: 'image',
      step: 3,
      stepLabel: 'Almacenamiento',
      title: 'OneDrive for Business',
      description: 'Estructura de carpetas generada automáticamente para organizar proyectos y archivos de cada cliente.'
    },
    { 
      url: '/flujo-gestion/paso4-powerautomate1.png', 
      label: 'Power Automate - Carpetas',
      type: 'image',
      step: 4,
      stepLabel: 'Automatización',
      title: 'Power Automate',
      description: 'Flujo 1: Creación automática de carpetas organizadas por cliente y proyecto en OneDrive.'
    },
    { 
      url: '/flujo-gestion/paso5-powerautomate2.png', 
      label: 'Power Automate - Correo',
      type: 'image',
      step: 5,
      stepLabel: 'Notificación',
      title: 'Power Automate',
      description: 'Flujo 2: Envío automático de correo electrónico al equipo con los detalles de la nueva solicitud.'
    }
  ];

  const nextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % implementationAssets.length);
  }, [implementationAssets.length]);

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + implementationAssets.length) % implementationAssets.length);
  };

  const handleManualInteraction = () => {
    setIsPaused(true);
    
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 8000);
  };

  const handleNext = () => {
    handleManualInteraction();
    nextSlide();
  };

  const handlePrev = () => {
    handleManualInteraction();
    prevSlide();
  };

  const handleDotClick = (index) => {
    handleManualInteraction();
    setActiveIndex(index);
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };

  useEffect(() => {
    if (!isPaused) {
      autoplayRef.current = setInterval(() => {
        nextSlide();
      }, 3000);
    }

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [isPaused, activeIndex, nextSlide]);

  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  // Gestión Digital carousel functions
  const nextGestionSlide = useCallback(() => {
    setActiveGestionIndex((prev) => (prev + 1) % gestionDigitalAssets.length);
  }, [gestionDigitalAssets.length]);

  const prevGestionSlide = () => {
    setActiveGestionIndex((prev) => (prev - 1 + gestionDigitalAssets.length) % gestionDigitalAssets.length);
  };

  const handleGestionManualInteraction = () => {
    setIsGestionPaused(true);
    
    if (gestionPauseTimeoutRef.current) {
      clearTimeout(gestionPauseTimeoutRef.current);
    }
    
    gestionPauseTimeoutRef.current = setTimeout(() => {
      setIsGestionPaused(false);
    }, 8000);
  };

  const handleGestionNext = () => {
    handleGestionManualInteraction();
    nextGestionSlide();
  };

  const handleGestionPrev = () => {
    handleGestionManualInteraction();
    prevGestionSlide();
  };

  const handleGestionDotClick = (index) => {
    handleGestionManualInteraction();
    setActiveGestionIndex(index);
  };

  const handleGestionTouchStart = (e) => {
    setGestionTouchStart(e.touches[0].clientX);
  };

  const handleGestionTouchEnd = (e) => {
    const touchEnd = e.changedTouches[0].clientX;
    const diff = gestionTouchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleGestionNext();
      } else {
        handleGestionPrev();
      }
    }
  };

  // Autoplay for Gestión Digital carousel
  useEffect(() => {
    if (!isGestionPaused) {
      gestionAutoplayRef.current = setInterval(() => {
        nextGestionSlide();
      }, 3000);
    }

    return () => {
      if (gestionAutoplayRef.current) {
        clearInterval(gestionAutoplayRef.current);
      }
    };
  }, [isGestionPaused, activeGestionIndex, nextGestionSlide]);

  useEffect(() => {
    return () => {
      if (gestionPauseTimeoutRef.current) {
        clearTimeout(gestionPauseTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className="projects section" id="projects">
      <div className="container">
        <div className="projects-header">
          <h2 className="display-medium">{t.projects.title}</h2>
          <p className="body-large projects-subtitle">{t.projects.subtitle}</p>
        </div>

        <div className="flujo-case">
          <div className="case-header-enhanced">
            <div className="case-header-content">
              <div className="case-meta">
                <span className="case-category">{t.projects.flujoCategory}</span>
              </div>
              <h3 className="case-main-title">{t.projects.flujoTitle}</h3>
              <p className="case-main-description">{t.projects.flujoDesc}</p>
            </div>
            <div className="case-accent-line"></div>
          </div>

          <div className="presencia-digital-section">
            <div className="section-title-bar">
              <h4 className="heading-2">Presencia Digital Conectada</h4>
            </div>
            
            <div className="presencia-content">
              <div className="presencia-explanation">
                <p className="body-medium explanation-text">
                  Construimos y conectamos los principales puntos de contacto digitales de Flujo SpA para que sitio web, redes sociales, Google y atención digital funcionen como un sistema.
                </p>

                <div className="connected-flow">
                  <div className="flow-item">
                    <div className="flow-icon">🌐</div>
                    <span className="body-small">Sitio Web</span>
                  </div>
                  <div className="flow-arrow">↓</div>
                  <div className="flow-item">
                    <div className="flow-icon">📱</div>
                    <span className="body-small">Instagram + Facebook</span>
                  </div>
                  <div className="flow-arrow">↓</div>
                  <div className="flow-item">
                    <div className="flow-icon">📊</div>
                    <span className="body-small">Meta Business Suite</span>
                  </div>
                  <div className="flow-arrow">↓</div>
                  <div className="flow-item">
                    <div className="flow-icon">🔍</div>
                    <span className="body-small">Google Business</span>
                  </div>
                  <div className="flow-arrow">↓</div>
                  <div className="flow-item">
                    <div className="flow-icon">💬</div>
                    <span className="body-small">Atención Digital</span>
                  </div>
                </div>

                <p className="body-small flow-caption">
                  Una presencia digital conectada, donde cada canal cumple una función y conduce al cliente hacia el contacto.
                </p>
              </div>

              <div className="presencia-visual">
                <div 
                  className="stacked-carousel"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="carousel-stack">
                    {implementationAssets.map((asset, index) => {
                      const position = (index - activeIndex + implementationAssets.length) % implementationAssets.length;
                      
                      let stackClass = '';
                      if (position === 0) stackClass = 'stack-front';
                      else if (position === 1) stackClass = 'stack-second';
                      else if (position === 2) stackClass = 'stack-third';
                      else stackClass = 'stack-hidden';

                      return (
                        <div
                          key={index}
                          className={`carousel-card ${stackClass}`}
                        >
                          <div className="asset-label">{asset.label}</div>
                          <img 
                            src={asset.url} 
                            alt={`${asset.label} implementation`}
                            className="asset-screenshot"
                          />
                        </div>
                      );
                    })}
                  </div>

                  <button 
                    className="carousel-control carousel-prev" 
                    onClick={handlePrev}
                    aria-label="Previous"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    className="carousel-control carousel-next" 
                    onClick={handleNext}
                    aria-label="Next"
                  >
                    <ChevronRight size={24} />
                  </button>

                  <div className="carousel-indicators">
                    {implementationAssets.map((_, index) => (
                      <button
                        key={index}
                        className={`indicator-dot ${index === activeIndex ? 'active' : ''}`}
                        onClick={() => handleDotClick(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="carousel-caption">
                  <span className="caption-category">{implementationAssets[activeIndex].category}</span>
                  <span className="caption-separator">·</span>
                  <span className="caption-title">{implementationAssets[activeIndex].title}</span>
                  <p className="caption-description">{implementationAssets[activeIndex].description}</p>
                </div>
              </div>
            </div>

            <div className="website-section">
              <div className="website-maintenance">
                <h5 className="heading-2">Sitio web y mantenimiento digital</h5>
                
                <div className="maintenance-columns">
                  <div className="maintenance-block">
                    <strong className="body-medium">Implementación Inicial</strong>
                    <p className="body-small">
                      Diseño, estructura, contenido inicial, conexión con WhatsApp y redes sociales, adaptación responsive y dominio.
                    </p>
                  </div>

                  <div className="maintenance-block">
                    <strong className="body-medium">Mantenimiento</strong>
                    <p className="body-small">
                      Actualización de fotografías, proyectos realizados, textos, información comercial, enlaces y pequeños ajustes de contenido.
                    </p>
                  </div>
                </div>

                <p className="body-small maintenance-statement">
                  No solo construimos la vitrina. También podemos mantenerla viva.
                </p>
              </div>

              <div className="website-video-card">
                <div className="video-label">Sitio web implementado</div>
                <div className="video-container">
                  <video 
                    ref={videoRef}
                    className="website-video"
                    poster="/flujo-assets/website.png"
                    onClick={toggleVideo}
                    onEnded={() => setVideoPlaying(false)}
                  >
                    <source src="/flujo-assets/website-video.mp4" type="video/mp4" />
                    Tu navegador no soporta el elemento de video.
                  </video>
                  <button 
                    className="video-play-button"
                    onClick={toggleVideo}
                    aria-label={videoPlaying ? 'Pausar' : 'Reproducir'}
                  >
                    {videoPlaying ? <Pause size={32} /> : <Play size={32} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Gestión Digital Conectada Section */}
          <div className="gestion-digital-section">
            <div className="section-title-bar">
              <h4 className="heading-2">Gestión Digital Conectada</h4>
            </div>
            
            <div className="gestion-content">
              <div className="gestion-explanation">
                <div className="system-workflow">
                  
                  <div className="workflow-stage">
                    <div className="stage-icon">📝</div>
                    <div className="stage-content">
                      <h5 className="stage-title">NUEVA SOLICITUD</h5>
                      <p className="stage-description">El equipo registra los datos una sola vez.</p>
                      <span className="stage-tech">Microsoft Forms</span>
                    </div>
                  </div>

                  <div className="workflow-connector">
                    <div className="connector-line"></div>
                    <div className="connector-arrow">↓</div>
                  </div>

                  <div className="workflow-stage">
                    <div className="stage-icon">📋</div>
                    <div className="stage-content">
                      <h5 className="stage-title">SE CREA EL PROYECTO</h5>
                      <p className="stage-description">La información queda registrada para organizar y hacer seguimiento del trabajo.</p>
                      <span className="stage-tech">Microsoft Lists</span>
                    </div>
                  </div>

                  <div className="workflow-connector">
                    <div className="connector-line"></div>
                    <div className="connector-arrow">↓</div>
                  </div>

                  <div className="workflow-stage">
                    <div className="stage-icon">📁</div>
                    <div className="stage-content">
                      <h5 className="stage-title">SE ORGANIZAN LOS ARCHIVOS</h5>
                      <p className="stage-description">El sistema crea automáticamente la estructura necesaria para guardar los documentos del proyecto.</p>
                      <span className="stage-tech">OneDrive</span>
                    </div>
                  </div>

                  <div className="workflow-connector">
                    <div className="connector-line"></div>
                    <div className="connector-arrow">↓</div>
                  </div>

                  <div className="workflow-stage">
                    <div className="stage-icon">⚙️</div>
                    <div className="stage-content">
                      <h5 className="stage-title">EL SISTEMA TRABAJA</h5>
                      <p className="stage-description">Las tareas repetitivas se ejecutan automáticamente y las distintas etapas quedan conectadas.</p>
                      <span className="stage-tech">Power Automate</span>
                    </div>
                  </div>

                  <div className="workflow-connector">
                    <div className="connector-line"></div>
                    <div className="connector-arrow">↓</div>
                  </div>

                  <div className="workflow-stage">
                    <div className="stage-icon">📧</div>
                    <div className="stage-content">
                      <h5 className="stage-title">LOS DOCUMENTOS LLEGAN DONDE CORRESPONDEN</h5>
                      <p className="stage-description">El sistema detecta documentos recibidos, extrae los archivos y actualiza la información del proyecto.</p>
                      <span className="stage-tech">Outlook + Power Automate</span>
                    </div>
                  </div>

                  <div className="workflow-connector">
                    <div className="connector-line"></div>
                    <div className="connector-arrow">↓</div>
                  </div>

                  <div className="workflow-result">
                    <h5 className="result-title">RESULTADO</h5>
                    <p className="result-main">Proyecto organizado, documentos almacenados y seguimiento actualizado.</p>
                    <p className="result-benefit">Menos trabajo manual. Más control sobre la operación.</p>
                  </div>

                </div>
              </div>

              <div className="gestion-visual">
                <div 
                  className="stacked-carousel"
                  onTouchStart={handleGestionTouchStart}
                  onTouchEnd={handleGestionTouchEnd}
                >
                  <div className="carousel-stack">
                    {gestionDigitalAssets.map((asset, index) => {
                      const position = (index - activeGestionIndex + gestionDigitalAssets.length) % gestionDigitalAssets.length;
                      
                      let stackClass = '';
                      if (position === 0) stackClass = 'stack-front';
                      else if (position === 1) stackClass = 'stack-second';
                      else if (position === 2) stackClass = 'stack-third';
                      else stackClass = 'stack-hidden';

                      return (
                        <div
                          key={index}
                          className={`carousel-card ${stackClass}`}
                        >
                          <div className="asset-label">{asset.label}</div>
                          <img 
                            src={asset.url} 
                            alt={`${asset.label} implementation`}
                            className="asset-screenshot"
                          />
                        </div>
                      );
                    })}
                  </div>

                  <button 
                    className="carousel-control carousel-prev" 
                    onClick={handleGestionPrev}
                    aria-label="Previous"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    className="carousel-control carousel-next" 
                    onClick={handleGestionNext}
                    aria-label="Next"
                  >
                    <ChevronRight size={24} />
                  </button>

                  <div className="carousel-indicators">
                    {gestionDigitalAssets.map((_, index) => (
                      <button
                        key={index}
                        className={`indicator-dot ${index === activeGestionIndex ? 'active' : ''}`}
                        onClick={() => handleGestionDotClick(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="carousel-caption">
                  <span className="caption-category">PASO {gestionDigitalAssets[activeGestionIndex].step}</span>
                  <span className="caption-separator">·</span>
                  <span className="caption-title">{gestionDigitalAssets[activeGestionIndex].title}</span>
                  <p className="caption-description">{gestionDigitalAssets[activeGestionIndex].description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Laundry Management System Project */}
          <div className="laundry-project">
            <div className="case-header">
              <div className="case-badge">{t.projects.laundryCategory}</div>
              <h3 className="heading-1">{t.projects.laundryTitle}</h3>
              <p className="body-medium case-description">{t.projects.laundryDesc}</p>
              <span className="project-status">{t.projects.laundryStatus}</span>
            </div>

            <div className="laundry-content">
              <div className="laundry-video-section">
                <div className="video-label">{t.projects.laundryVideoLabel}</div>
                <div className="laundry-video-container">
                  <video 
                    className="laundry-video"
                    controls
                    preload="metadata"
                  >
                    <source src="/lavanderia-assets/demo-sistema.mp4" type="video/mp4" />
                    Tu navegador no soporta el elemento de video.
                  </video>
                </div>
              </div>

              <div className="laundry-explanation">
                <p className="body-medium">{t.projects.laundryExplanation}</p>
                <div className="laundry-solutions">
                  <h5 className="heading-2">Áreas de la solución:</h5>
                  <ul className="solution-list">
                    {t.projects.laundrySolutions.map((solution, index) => (
                      <li key={index} className="body-medium">{solution}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
